import { Component, Input, OnInit, OnDestroy } from '@angular/core';
import { Customer } from '@monsido/modules/models/api/customer';
import { ActiveDialog } from '@monsido/angular-shared-components';
import { Observable, Subscription } from 'rxjs';
import { DomainImportWizardService } from '../../services/domain-import-wizard.service';
import {
    WizardStep,
    ImportProgress,
    ImportConfiguration,
    ParsedImportFile,
    ImportSummary,
} from '../../types/domain-management.types';

@Component({
    selector: 'mon-import-wizard',
    templateUrl: './import-wizard.component.html',
    standalone: false,
})
export class FormImportWizardComponent implements OnInit, OnDestroy {
    @Input() customer: Customer;
    @Input() monIsBackendAdmin: boolean = false;

    currentStep$: Observable<WizardStep>;
    progress$: Observable<ImportProgress>;
    configuration$: Observable<ImportConfiguration>;
    parsedFile$: Observable<ParsedImportFile | null>;
    importSummary$: Observable<ImportSummary | null>;

    WizardStep = WizardStep;

    loading = false;
    error: string | null = null;

    private subscriptions: Subscription[] = [];

    constructor (
        private activeDialog: ActiveDialog,
        private wizardService: DomainImportWizardService,
    ) {
        this.currentStep$ = this.wizardService.currentStep$;
        this.progress$ = this.wizardService.progress$;
        this.configuration$ = this.wizardService.configuration$;
        this.parsedFile$ = this.wizardService.parsedFile$;
        this.importSummary$ = this.wizardService.importSummary$;
    }

    ngOnInit (): void {
        this.wizardService.resetWizard();
    }

    ngOnDestroy (): void {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }

    /**
     * Handle file upload from upload step
     */
    onFileUploaded (file: File): void {
        this.loading = true;
        this.error = null;

        this.wizardService.parseImportFile(file)
            .then((result) => {
                if (result.isValid) {
                    this.wizardService.nextStep();
                } else {
                    this.error = result.errors.join(', ');
                }
            })
            .catch((error) => {
                this.error = `Failed to parse file: ${error?.message || 'Unknown error'}`;
            })
            .finally(() => {
                this.loading = false;
            });
    }

    /**
     * Handle configuration updates from configure step
     */
    onConfigurationUpdated (config: Partial<ImportConfiguration>): void {
        this.wizardService.updateConfiguration(config);
    }

    /**
     * Navigate to next step
     */
    nextStep (): void {
        this.wizardService.nextStep();
    }

    /**
     * Navigate to previous step
     */
    previousStep (): void {
        this.wizardService.previousStep();
    }

    /**
     * Start the import process
     */
    startImport (): void {
        if (!this.customer?.id) {
            this.error = 'Customer ID is required for import';
            return;
        }

        this.loading = true;
        this.error = null;

        this.wizardService.startImport(this.customer.id)
            .then(() => {
            })
            .catch((error) => {
                this.error = `Import failed: ${error?.message || 'Unknown error'}`;
                this.loading = false;
            });
    }

    /**
     * Download error report
     */
    downloadErrorReport (): void {
        this.wizardService.downloadErrorReport();
    }

    /**
     * Close the dialog
     */
    close (): void {
        this.activeDialog.close();
    }

    /**
     * Close dialog with refresh callback
     */
    closeWithRefresh (): void {
        this.activeDialog.close(true); // Return true to indicate refresh needed
    }

    /**
     * Check if we can navigate to next step
     */
    canProceedToNext (step: WizardStep, parsedFile: ParsedImportFile | null): boolean {
        switch (step) {
            case WizardStep.UPLOAD:
                return parsedFile?.isValid === true;
            case WizardStep.CONFIGURE:
                return true; // Configuration is always optional
            case WizardStep.CONFIRM:
                return parsedFile?.isValid === true;
            default:
                return false;
        }
    }

    /**
     * Check if we can navigate to previous step
     */
    canGoBack (step: WizardStep): boolean {
        return step !== WizardStep.UPLOAD && step !== WizardStep.PROGRESS;
    }

    /**
     * Get step title for display
     */
    getStepTitle (step: WizardStep): string {
        switch (step) {
            case WizardStep.UPLOAD:
                return 'Upload File';
            case WizardStep.CONFIGURE:
                return 'Import Options';
            case WizardStep.CONFIRM:
                return 'Confirm Import';
            case WizardStep.PROGRESS:
                return 'Importing Domains';
            case WizardStep.RESULTS:
                return 'Import Results';
            default:
                return 'Import Domains';
        }
    }

    /**
     * Get step number for display
     */
    getStepNumber (step: WizardStep): number {
        const steps = Object.values(WizardStep);
        return steps.indexOf(step) + 1;
    }

    /**
     * Get total number of steps
     */
    getTotalSteps (): number {
        return Object.values(WizardStep).length;
    }
}
